import { SafePromise } from "./SafePromise"

/**
 * Executes a promise with guaranteed cleanup callback execution.
 * The cleanup function is called whether the promise succeeds or fails.
 *
 * @param input - The promise to execute
 * @param close - Cleanup function called with error (if any) after promise settles
 * @returns A promise that resolves to the original promise's value
 * @throws The original error if the promise rejects
 *
 * @example
 * ```ts
 * import { WithCleanup } from "@pdfking/tfunc"
 *
 * const resource = openResource();
 *
 * try {
 *   const result = await WithCleanup(
 *     processResource(resource),
 *     (error) => {
 *       resource.close();
 *       if (error) {
 *         console.error('Processing failed:', error);
 *       }
 *     }
 *   );
 *   return result;
 * } catch (error) {
 *   // Cleanup already ran
 *   throw error;
 * }
 * ```
 */
export const WithCleanup = async <T>(
  input: Promise<T>,
  close: (error: Error | undefined) => void
): Promise<T> => {
  const { error, value } = await SafePromise(input)

  void close(error)

  if (error) {
    throw error
  }

  return value
}
